/**
 * Billing model selector component for project billing configuration.
 *
 * This component handles the selection of billing models (Time & Material, Fixed Price, Non-billable)
 * with business rule validation and user warnings for model transitions. It provides a radio group
 * interface with conditional warnings when changing from certain billing models.
 *
 * Features:
 * - Radio button selection for billing model options
 * - Conditional warnings for billing model transitions (e.g., from Non-billable)
 * - Business rule enforcement through option disabling
 * - Read-only mode support for view contexts
 * - Integration with React Hook Form for validation and state management
 * - Internationalization support for labels and messages
 *
 * Business Logic:
 * - Some billing model transitions may be restricted based on project state
 * - Warning alerts inform users about implications of billing model changes
 * - Options can be conditionally disabled based on current project configuration
 */
import { AlertTriangle } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { CustomRadioGroup } from '@/modules/common';
import { BillingModelOption, ProjectBillingFormData } from '@/modules/projects';
import { ProjectBillingModel } from '@/modules/projects/types/project';

/**
 * Props interface for the BillingModelSelector component.
 * Includes form integration, options configuration, and display state.
 */
export interface BillingModelSelectorProps {
  /** React Hook Form instance for billing data management */
  form: ReturnType<typeof useForm<ProjectBillingFormData>>;
  /** Whether the component should be in read-only mode */
  isReadMode: boolean;
  /** Available billing model options with potential restrictions */
  billingModelOptions: BillingModelOption[];
  /** Whether to show transition warning alert */
  showWarning: boolean;
  /** Currently selected billing model for conditional logic */
  currentBillingModel: ProjectBillingModel;
  /** Translation function for internationalized text */
  t: (key: string) => string;
}

/**
 * Billing model selector component with integrated warning system.
 *
 * Renders a radio group for billing model selection with conditional warning alerts.
 * The component adapts its behavior based on current project state and user permissions,
 * providing appropriate feedback for billing model transitions.
 *
 * Warning Logic:
 * - Shows warnings when transitioning from Non-billable to billable models
 * - Alerts users about potential implications of billing model changes
 * - Provides context-appropriate messaging based on current selection
 *
 * @param props - Component props including form state and configuration options
 * @returns Billing model selector with optional warning alerts
 */
export function BillingModelSelector({
  form,
  isReadMode,
  billingModelOptions,
  showWarning,
  t,
}: BillingModelSelectorProps) {
  return (
    <div className="space-y-3">
      {/* Main billing model selection radio group */}
      <CustomRadioGroup
        control={form.control}
        name="billingModel"
        disabled={isReadMode}
        label={t('common.billingModel')}
        options={billingModelOptions}
      />

      {/* Conditional warning alert for billing model transitions */}
      {showWarning && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{t('details.tabs.billing.billingModelChangeWarning')}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
